import React, { useState } from 'react';
import { 
  Search, 
  Filter, 
  Star, 
  Download, 
  Eye, 
  Users, 
  Clock,
  Tag,
  Grid,
  List,
  Zap,
  Mail,
  MessageSquare,
  TrendingUp,
  UserCheck,
  Globe
} from 'lucide-react';
import { WorkflowTemplate, TemplateCategory } from '../types/workflow';

interface WorkflowTemplateGalleryProps {
  templates: WorkflowTemplate[];
  onUseTemplate: (template: WorkflowTemplate) => void;
}

export const WorkflowTemplateGallery: React.FC<WorkflowTemplateGalleryProps> = ({
  templates,
  onUseTemplate
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory | 'all'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'popular' | 'recent' | 'rating'>('popular');

  const categories: { id: TemplateCategory | 'all'; label: string; icon: React.ComponentType<any> }[] = [
    { id: 'all', label: 'All Templates', icon: Grid },
    { id: 'marketing', label: 'Marketing', icon: TrendingUp },
    { id: 'sales', label: 'Sales', icon: Users },
    { id: 'customer_service', label: 'Customer Service', icon: MessageSquare },
    { id: 'hr', label: 'Human Resources', icon: UserCheck },
    { id: 'finance', label: 'Finance', icon: TrendingUp },
    { id: 'operations', label: 'Operations', icon: Globe },
    { id: 'development', label: 'Development', icon: Zap },
    { id: 'general', label: 'General', icon: Grid }
  ];

  // Mock templates for demonstration
  const mockTemplates: WorkflowTemplate[] = [
    {
      id: 'template-1',
      name: 'Welcome Email Sequence',
      description: 'Automated welcome email series for new customers with personalized content and follow-ups',
      category: 'marketing',
      tags: ['email', 'onboarding', 'automation'],
      definition: {
        steps: [],
        variables: [],
        settings: {}
      },
      triggers: [],
      thumbnail: '',
      isPublic: true,
      usageCount: 1250,
      rating: 4.8,
      createdBy: 'Bombard Team',
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-01-20')
    },
    {
      id: 'template-2',
      name: 'Lead Qualification Process',
      description: 'Automatically qualify and route leads based on criteria, send notifications to sales team',
      category: 'sales',
      tags: ['lead', 'qualification', 'routing'],
      definition: {
        steps: [],
        variables: [],
        settings: {}
      },
      triggers: [],
      thumbnail: '',
      isPublic: true,
      usageCount: 890,
      rating: 4.6,
      createdBy: 'Bombard Team',
      createdAt: new Date('2024-01-10'),
      updatedAt: new Date('2024-01-18')
    },
    {
      id: 'template-3',
      name: 'Customer Support Ticket Routing',
      description: 'Intelligent ticket routing based on priority, category, and agent availability',
      category: 'customer_service',
      tags: ['support', 'routing', 'automation'],
      definition: {
        steps: [],
        variables: [],
        settings: {}
      },
      triggers: [],
      thumbnail: '',
      isPublic: true,
      usageCount: 675,
      rating: 4.7,
      createdBy: 'Bombard Team',
      createdAt: new Date('2024-01-08'),
      updatedAt: new Date('2024-01-16')
    },
    {
      id: 'template-4',
      name: 'Employee Onboarding Checklist',
      description: 'Complete onboarding workflow with task assignments, document collection, and progress tracking',
      category: 'hr',
      tags: ['onboarding', 'hr', 'checklist'],
      definition: {
        steps: [],
        variables: [],
        settings: {}
      },
      triggers: [],
      thumbnail: '',
      isPublic: true,
      usageCount: 445,
      rating: 4.5,
      createdBy: 'Bombard Team',
      createdAt: new Date('2024-01-05'),
      updatedAt: new Date('2024-01-14')
    },
    {
      id: 'template-5',
      name: 'Invoice Processing Automation',
      description: 'Automated invoice processing with approval workflows and payment reminders',
      category: 'finance',
      tags: ['invoice', 'approval', 'finance'],
      definition: {
        steps: [],
        variables: [],
        settings: {}
      },
      triggers: [],
      thumbnail: '',
      isPublic: true,
      usageCount: 320,
      rating: 4.4,
      createdBy: 'Bombard Team',
      createdAt: new Date('2024-01-03'),
      updatedAt: new Date('2024-01-12')
    },
    {
      id: 'template-6',
      name: 'Social Media Content Approval',
      description: 'Content review and approval workflow for social media posts with scheduling',
      category: 'marketing',
      tags: ['social media', 'approval', 'content'],
      definition: {
        steps: [],
        variables: [],
        settings: {}
      },
      triggers: [],
      thumbnail: '',
      isPublic: true,
      usageCount: 280,
      rating: 4.3,
      createdBy: 'Bombard Team',
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-01-10')
    }
  ];

  const allTemplates = [...templates, ...mockTemplates];

  const filteredTemplates = allTemplates
    .filter(template => {
      const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return b.usageCount - a.usageCount;
        case 'recent':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        case 'rating':
          return b.rating - a.rating;
        default:
          return 0;
      }
    });

  const getCategoryIcon = (category: TemplateCategory) => {
    const categoryData = categories.find(c => c.id === category);
    return categoryData?.icon || Grid;
  };

  const getCategoryColor = (category: TemplateCategory) => {
    const colors: Record<TemplateCategory, string> = {
      marketing: 'bg-pink-500/20 text-pink-400 border-pink-400/30',
      sales: 'bg-green-500/20 text-green-400 border-green-400/30',
      customer_service: 'bg-blue-500/20 text-blue-400 border-blue-400/30',
      hr: 'bg-purple-500/20 text-purple-400 border-purple-400/30',
      finance: 'bg-yellow-500/20 text-yellow-400 border-yellow-400/30',
      operations: 'bg-orange-500/20 text-orange-400 border-orange-400/30',
      development: 'bg-cyan-500/20 text-cyan-400 border-cyan-400/30',
      general: 'bg-neutral-500/20 text-neutral-400 border-neutral-400/30'
    };
    return colors[category] || colors.general;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Template Gallery</h2>
          <p className="text-neutral-400">Pre-built workflows to get you started quickly</p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-neutral-400 hover:text-white'
            }`}
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-neutral-400 hover:text-white'
            }`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Category Filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value as TemplateCategory | 'all')}
          className="px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {categories.map(category => (
            <option key={category.id} value={category.id}>
              {category.label}
            </option>
          ))}
        </select>

        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'popular' | 'recent' | 'rating')}
          className="px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="popular">Most Popular</option>
          <option value="recent">Recently Updated</option>
          <option value="rating">Highest Rated</option>
        </select>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        {categories.map(category => {
          const IconComponent = category.icon;
          const isSelected = selectedCategory === category.id;
          
          return (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
                isSelected
                  ? 'bg-blue-600 text-white border-blue-500'
                  : 'bg-neutral-800/50 text-neutral-400 border-neutral-700 hover:text-white hover:border-neutral-600'
              }`}
            >
              <IconComponent className="w-4 h-4" />
              <span className="text-sm font-medium">{category.label}</span>
            </button>
          );
        })}
      </div>

      {/* Templates Grid/List */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-neutral-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-neutral-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No templates found</h3>
          <p className="text-neutral-400">Try adjusting your search or filter criteria</p>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
          {filteredTemplates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              viewMode={viewMode}
              onUse={() => onUseTemplate(template)}
              getCategoryColor={getCategoryColor}
              getCategoryIcon={getCategoryIcon}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface TemplateCardProps {
  template: WorkflowTemplate;
  viewMode: 'grid' | 'list';
  onUse: () => void;
  getCategoryColor: (category: TemplateCategory) => string;
  getCategoryIcon: (category: TemplateCategory) => React.ComponentType<any>;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  viewMode,
  onUse,
  getCategoryColor,
  getCategoryIcon
}) => {
  const CategoryIcon = getCategoryIcon(template.category);
  const categoryColorClass = getCategoryColor(template.category);

  if (viewMode === 'list') {
    return (
      <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6 hover:border-neutral-700 transition-all duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <div className={`p-3 rounded-lg border ${categoryColorClass}`}>
              <CategoryIcon className="w-6 h-6" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold text-white truncate">{template.name}</h3>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                  <span className="text-sm text-neutral-300">{template.rating}</span>
                </div>
              </div>
              
              <p className="text-neutral-400 text-sm mb-3 line-clamp-2">{template.description}</p>
              
              <div className="flex items-center gap-4 text-xs text-neutral-400">
                <div className="flex items-center gap-1">
                  <Download className="w-3 h-3" />
                  <span>{template.usageCount.toLocaleString()} uses</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  <span>Updated {new Date(template.updatedAt).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  <span>by {template.createdBy}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 ml-4">
            <button className="p-2 text-neutral-400 hover:text-white rounded-lg transition-colors">
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={onUse}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Use Template
            </button>
          </div>
        </div>
        
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mt-4">
          {template.tags.map(tag => (
            <span
              key={tag}
              className="px-2 py-1 bg-neutral-800 text-neutral-300 text-xs rounded-md flex items-center gap-1"
            >
              <Tag className="w-3 h-3" />
              {tag}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl overflow-hidden hover:border-neutral-700 transition-all duration-200 group">
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-2 rounded-lg border ${categoryColorClass}`}>
            <CategoryIcon className="w-5 h-5" />
          </div>
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 text-yellow-400 fill-current" />
            <span className="text-sm text-neutral-300">{template.rating}</span>
          </div>
        </div>
        
        <h3 className="text-lg font-semibold text-white mb-2 line-clamp-1">{template.name}</h3>
        <p className="text-neutral-400 text-sm line-clamp-3 mb-4">{template.description}</p>
        
        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-4">
          {template.tags.slice(0, 3).map(tag => (
            <span
              key={tag}
              className="px-2 py-1 bg-neutral-800 text-neutral-300 text-xs rounded-md"
            >
              {tag}
            </span>
          ))}
          {template.tags.length > 3 && (
            <span className="px-2 py-1 bg-neutral-800 text-neutral-400 text-xs rounded-md">
              +{template.tags.length - 3}
            </span>
          )}
        </div>
      </div>
      
      {/* Footer */}
      <div className="px-6 pb-6">
        <div className="flex items-center justify-between text-xs text-neutral-400 mb-4">
          <div className="flex items-center gap-1">
            <Download className="w-3 h-3" />
            <span>{template.usageCount.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{new Date(template.updatedAt).toLocaleDateString()}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button className="flex-1 flex items-center justify-center gap-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 hover:text-white px-4 py-2 rounded-lg transition-colors">
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <button
            onClick={onUse}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Use
          </button>
        </div>
      </div>
    </div>
  );
}; 